import * as React from 'react'
import { convoTypeToIconName } from '../../../../common/src/components/Icon'
import { isoDateFmt, diffFmt } from '../../../../common/src/date'
import { capitalize, formatOptPhoneNumber } from '../../../../common/src/format'
import {
  ConversationType,
  ActionInput,
  ActionName,
  ContactInput,
  FetchThreadQuery,
  CommandType,
  Language,
} from '../../../../common/src/types.gen'
import validate from '../../../../common/src/validate'
import { postAction, updateContact } from '../../graphql/mutations'
import { Field } from '../components/EditBox'
import EditBoxes from '../components/EditBoxes'

const conversationTypes = Object.keys(ConversationType) as ConversationType[]

export default class ContactEditBoxes extends React.PureComponent<{
  thread: FetchThreadQuery['thread']
}> {
  render() {
    const { thread } = this.props
    const contact = thread.contactThem

    const { conversationType, moveInDate, additionalOccupants, messagesWithLinks } = thread
    const { addressPhone, addressEmail, preferMessageType, customAgentNamePhone, workHours, language } = contact
    const model = {
      conversationType,
      addressPhone,
      addressEmail,
      preferMessageType,
      customAgentNamePhone,
      workHours,
      language,
      moveInDate,
      additionalOccupants,
      waitingForShowing: messagesWithLinks.waitingForShowing,
      calendar: thread.calendar,
    }

    const fieldConvoType: Field<ConversationType> = {
      key: 'conversationType',
      isEditable: true,
      options: conversationTypes,
      iconFunc: (val?: ConversationType) => val && convoTypeToIconName(val),
      tooltip:
        "Lisa chooses her language based on this thread's conversation type:\n\n" +
        'PROSPECT — wants to book a showing\n\n' +
        'AGENT — performs showings, works for the firm\n\n' +
        'BOSS — sets policy, manages agents, works for the firm\n\n' +
        'INVESTOR — only wants reports, doesn’t set policy like a BOSS.\n\n' +
        'UNPARSED — this is a mega thread full of unparsed leads.\n\n' +
        'OTHER — Spam: none of the above. Lisa ignores OTHER threads entirely, so be careful.\n\n' +
        'RESIDENT — Already lives at property, might have contacted Lisa accidentally through a listing',
    }
    const fieldPreferPhone: Field<string> = {
      key: 'preferMessageType',
      isEditable: true,
      options: ['EMAIL', 'SMS'],
      textFunc: val => 'Prefer ' + val,
      editTextFunc: val => val || '',
    }
    const fieldAddrPhone: Field<string> = {
      key: 'addressPhone',
      isEditable: contact.addressPhone == null,
      textFunc: val => formatOptPhoneNumber(val) || 'No phone',
      editTextFunc: val => val || '',
      validator: validate.phone().empty(''),
      placeholder: '650-123-4567',
      iconFunc: () => 'channel-sms',
    }
    const fieldAddrEmail: Field<string> = {
      key: 'addressEmail',
      isEditable: contact.addressEmail == null,
      textFunc: val => val || 'No email',
      editTextFunc: val => val || '',
      validator: validate.email().empty(''),
      placeholder: 'example@gmail.com',
      iconFunc: () => 'channel-email',
    }
    const fieldMoveInDate: Field<string> = {
      key: 'moveInDate',
      isEditable: true,
      textFunc: val => (val != null && val > new Date().toISOString() ? val + ' desired move in' : 'asap move in'),
      validator: validate.isoDate().empty(''),
      editTextFunc: val => val || '',
      colorFunc: val => (val != null ? 'mint' : undefined),
      placeholder: isoDateFmt(new Date()),
    }
    const fieldAdditionalOccupants: Field<number> = {
      key: 'additionalOccupants',
      isEditable: true,
      textFunc: val => (val != null && val >= 0 ? '+' + val + ' occupants' : '+0 occupants'),
      editTextFunc: val => '' + (val || ''),
      validator: validate.integer(n => n >= 0),
      colorFunc: val => (val != null ? 'mint' : undefined),
    }
    const fieldLanguage: Field<Language> = {
      key: 'language',
      isEditable: true,
      textFunc: val => (val != null ? 'speaks ' + capitalize(val.toString()) : 'speaks English'),
      editTextFunc: val => '' + (val || ''),
      validator: validate.options(Language),
      iconFunc: (val: Language) => (!val || val === Language.ENGLISH ? undefined : 'contact-language'),
    }
    const fieldWaitingForShowing: Field<{ timeLastAccess: string }> = {
      key: 'waitingForShowing',
      isEditable: false,
      textFunc: val => (val ? 'Waiting on showing' : 'Not waiting on showing'),
      colorFunc: val => (val ? 'silver' : 'midway'),
    }
    const fieldCustomAgentNamePhone: Field<string> = {
      key: 'customAgentNamePhone',
      isEditable: true,
      textFunc: val => (val ? '"Our agent ' + val + ' will be there"' : 'Custom name?'),
      editTextFunc: val => val || '',
      validator: validate.str(),
      placeholder: 'Jim (123) 123-9999',
      tooltip:
        "Normally we give prospects the agent's first name and phone before " +
        'each showing. For example, "《Jim (650) 123-4567》 will meet you there." ' +
        'Customize this if the agent wants to keep their phone number private, ' +
        'use an office number, go by a different name, etc.',
    }
    const fieldCal: Field<{ timeLastAccess: string }> = {
      key: 'calendar',
      isEditable: false,
      textFunc: val =>
        val != null && val.timeLastAccess != null
          ? 'Cal ✓ ' + diffFmt(new Date(), val.timeLastAccess) + ' ago'
          : 'No cal',
    }
    const fieldWorkHours: Field<string> = {
      key: 'workHours',
      isEditable: true,
      textFunc: val => (val === '' ? 'No default work hours' : 'Default work hours: ' + val),
      editTextFunc: val => val || '',
      placeholder: 'M-F 9am-5pm',
      tooltip: 'Example format: M-Th 9am-5pm F Su 9am-5pm 7pm-9pm',
    }

    const isProspect = thread.conversationType === ConversationType.PROSPECT
    const isAgent = thread.conversationType === ConversationType.AGENT

    const fields = [
      fieldConvoType,
      contact.addressPhone != null && contact.addressEmail != null && fieldPreferPhone,
      fieldAddrPhone,
      fieldAddrEmail,
      isProspect && fieldMoveInDate,
      isProspect && fieldAdditionalOccupants,
      fieldLanguage,
      isProspect && fieldWaitingForShowing,
      isAgent && fieldCustomAgentNamePhone,
      isAgent && fieldCal,
      isAgent && fieldWorkHours,
    ].filter(f => f) as Field<any>[]

    return <EditBoxes model={model} fields={fields} displayType="small" onEdit={this.onEditBound} />
  }

  private onEditBound = (key: string, value: string) => {
    if (key === 'conversationType') {
      const action: ActionInput = {
        actionName: ActionName.MARK_CONVO_TYPE,
        commandJsons: [
          JSON.stringify({
            commandType: CommandType.UPDATE_THREAD,
            threadId: this.props.thread.id,
            setConversationType: value as ConversationType,
          }),
        ],
      }
      const variables = { threadId: this.props.thread.id, action }
      return postAction(variables, 'Saved convo type')
    } else if (key === 'moveInDate') {
      // deleting is equivalent to asap / a date in the past
      if (value === '') {
        value = '2000-01-01'
      }
      const action: ActionInput = {
        actionName: ActionName.SET_MOVE_IN_DATE,
        commandJsons: [
          JSON.stringify({
            commandType: CommandType.UPDATE_THREAD,
            threadId: this.props.thread.id,
            moveInDate: value,
          }),
        ],
      }
      const variables = { threadId: this.props.thread.id, action }
      return postAction(variables, 'Saved move-in')
    } else if (key === 'additionalOccupants') {
      const action: ActionInput = {
        actionName: ActionName.SET_ADDITIONAL_OCCUPANTS,
        commandJsons: [
          JSON.stringify({
            commandType: CommandType.UPDATE_THREAD,
            threadId: this.props.thread.id,
            additionalOccupants: value,
          }),
        ],
      }
      const variables = { threadId: this.props.thread.id, action }
      return postAction(variables, 'Saved additional occupants count')
    } else {
      const input: ContactInput = { id: this.props.thread.contactThem.id, [key]: value }
      return updateContact(this.props.thread.contactThem, input)
    }
  }
}
